import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  FaCog,
  FaShieldAlt,
  FaGlobe,
  FaChartLine,
  FaUsers,
  FaPhoneAlt,
  FaArrowRight,
  FaClock,
  FaDownload,
  FaCheckCircle,
  FaLaptop,
  FaServer,
  FaCloud,
  FaCode,
  FaHeadset,
  FaTools,
  FaDatabase,
  FaMobile,
  FaDesktop,
  FaWifi,
  FaStar,
  FaEuroSign,
  FaExchangeAlt,
  FaPalette,
} from "react-icons/fa";
import "./ServicesPage.css";

const ServicesPage = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Dynamic calculation of years since company founding
  const yearsInBusiness = new Date().getFullYear() - 1989;
  
  const services = [
    {
      icon: FaCode,
      title: "Vývoj na mieru",
      description:
        "Zákazkový vývoj softvérových riešení prispôsobených vašim potrebám",
    },
    {
      icon: FaExchangeAlt,
      title: "Migrácia systémov",
      description: "Migrácia systémov do moderných technológií a platforiem",
    },
    {
      icon: FaPalette,
      title: "Modernizácia web stránok",
      description:
        "Modernizácia web stránok a aplikácií s najnovšími technológiami",
    },
    {
      icon: FaCog,
      title: "IT Outsourcing",
      description:
        "Kompletná správa informačných technológií pre vaše podnikanie",
    },
    {
      icon: FaShieldAlt,
      title: "Bezpečnosť",
      description: "Zabezpečenie údajov a ochrana pred kybernetickými hrozbami",
    },
    {
      icon: FaGlobe,
      title: "Webhosting",
      description: "Spoľahlivý hosting pre vaše webové stránky a aplikácie",
    },
    {
      icon: FaChartLine,
      title: "Analýzy",
      description: "Business intelligence a analytické riešenia",
    },
  ];

  const mainServices = [
    {
      id: "outsourcing",
      icon: FaCog,
      title: "IT Outsourcing",
      subtitle: "Kompletná správa IT",
      description:
        "Prenechajte správu vašej IT infraštruktúry odborníkom a sústreďte sa na podnikanie",
      features: [
        "Správa serverov a sietí",
        "Údržba softvéru a hardvéru",
        "Technická podpora 24/7",
        "Monitoring systémov",
        "Backup a recovery",
        "Aktualizácie a záplaty",
      ],
      gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      price: "Od 299€/mesiac",
      link: "/sluzby/outsourcing",
    },
    {
      id: "security",
      icon: FaShieldAlt,
      title: "Kybernetická bezpečnosť",
      subtitle: "Ochrana vašich dát",
      description:
        "Komplexné bezpečnostné riešenia na ochranu pred kybernetickými hrozbami",
      features: [
        "Antivírus a anti-malware",
        "Firewall konfigurácia",
        "Bezpečnostné audity",
        "Penetračné testovanie",
        "Školenia zamestnancov",
        "Incident response",
      ],
      gradient: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
      price: "Od 199€/mesiac",
      link: "/bezpecnost",
    },
    {
      id: "hosting",
      icon: FaGlobe,
      title: "Webhosting a domény",
      subtitle: "Spoľahlivý hosting",
      description:
        "Profesionálny hosting pre webové stránky, aplikácie a email služby",
      features: [
        "Dedikovaný hosting",
        "Registrácia domén",
        "SSL certifikáty",
        "Email hosting",
        "Webmail prístup",
        "Zálohovanie dát",
      ],
      gradient: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
      price: "Od 15€/mesiac",
      link: "/webhosting",
    },
    {
      id: "development",
      icon: FaCode,
      title: "Vývoj softvéru",
      subtitle: "Custom riešenia",
      description:
        "Vývoj softvéru na mieru podľa vašich špecifických požiadaviek",
      features: [
        "Webové aplikácie",
        "Mobilné aplikácie",
        "Desktop softvér",
        "API integrácie",
        "Database dizajn",
        "Testovanie a QA",
      ],
      gradient: "linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)",
      price: "Od 50€/hodina",
      link: "/sluzby/vyvoj",
    },
  ];

  const supportLevels = [
    {
      name: "Basic Support",
      price: "99",
      period: "/mesiac",
      description: "Pre malé firmy",
      features: [
        "Email podpora 8-17h",
        "Vzdialená pomoc",
        "Základné poradenstvo",
        "Reakcia do 24h",
        "Dokumentácia",
      ],
      color: "#6c757d",
    },
    {
      name: "Professional Support",
      price: "199",
      period: "/mesiac",
      description: "Pre stredné firmy",
      features: [
        "Email + telefón 7-19h",
        "Prioritná podpora",
        "Proaktívny monitoring",
        "Reakcia do 4h",
        "Mesačné reporty",
        "Školenia",
      ],
      color: "#667eea",
      popular: true,
    },
    {
      name: "Enterprise Support",
      price: "399",
      period: "/mesiac",
      description: "Pre veľké firmy",
      features: [
        "Podpora 24/7/365",
        "Dedikovaný technik",
        "SLA garantovaná",
        "Reakcia do 1h",
        "Onsite podpora",
        "Konzultácie",
      ],
      color: "#f5576c",
    },
  ];

  const industries = [
    {
      icon: FaLaptop,
      title: "Malé a stredné firmy",
      description: "Komplexné IT riešenia pre rastúce spoločnosti",
    },
    {
      icon: FaServer,
      title: "Veľké korporácie",
      description: "Enterprise riešenia a pokročilé technológie",
    },
    {
      icon: FaMobile,
      title: "E-commerce",
      description: "Špecializované riešenia pre online obchody",
    },
    {
      icon: FaDatabase,
      title: "Dátové centrá",
      description: "Správa a optimalizácia dátových centier",
    },
  ];

  const testimonials = [
    {
      name: "Ing. Tomáš Svoboda",
      company: "TechCorp s.r.o.",
      position: "IT Director",
      text: "INVER nám poskytuje výborné IT služby už 5 rokov. Ich proaktívny prístup nám ušetril množstvo času a nákladov.",
      rating: 5,
    },
    {
      name: "Mgr. Anna Krejčová",
      company: "DataSystems Ltd.",
      position: "CEO",
      text: "Bezpečnostné riešenia od INVER-u sú na najvyššej úrovni. Cítime sa v bezpečí.",
      rating: 5,
    },
    {
      name: "Peter Novotný",
      company: "WebShop s.r.o.",
      position: "CTO",
      text: "Hosting služby sú spoľahlivé a rýchle. Podpora je vždy ochotná pomôcť.",
      rating: 5,
    },
  ];

  const stats = [
    { value: '500+', label: 'Spokojných klientov' },
    { value: '100+', label: 'Dokončených projektov' },
    { value: '99.9%', label: 'Uptime garancia' },
    { value: `${yearsInBusiness}+`, label: "Rokov skúseností" },
  ];

  return (
    <div className="services-page">
      {/* Hero Section */}
      <section className="services-hero">
        <h1>IT Služby</h1>
        <div className="hero-stats">
          {stats.map((stat, index) => (
            <div key={index} className="hero-stat">
              <div className="stat-value">{stat.value}</div>
              <div className="stat-label">{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Services Introduction Section */}
      <section className="services-intro">
        <div className="container">
          <div className="intro-content">
            <h2>Komplexné riešenia pre váš úspech</h2>
            <p>
              Ponúkame komplexné IT riešenia, ktoré plne pokrývajú vaše potreby – od vývoja systémov na mieru, cez ich modernizáciu a migráciu, až po bezpečnú a spoľahlivú prevádzku. Pomáhame firmám rásť prostredníctvom technológií, ktoré zefektívňujú ich podnikanie a prinášajú reálne výsledky.
            </p>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="services">
        <div className="container">
          <div className="section-header">
            <h2>Naše služby</h2>
            <p>Komplexné IT služby pre vaše podnikanie</p>
          </div>

          <div className="services-grid">
            {services.map((service, index) => (
              <div key={index} className="service-card">
                <div className="service-icon">
                  <service.icon />
                </div>
                <h4>{service.title}</h4>
                <p>{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Skills Section */}
      <section className="skills">
        <div className="container">
          <div className="section-header">
            <h2>Naše zručnosti a technológie</h2>
            <p>Ovládame širokú škálu programovacích jazykov a technológií</p>
          </div>

          <div className="skills-content">
            <div className="skills-category">
              <h3>Programovacie jazyky a technológie</h3>
              <div className="tech-table-container">
                <table className="tech-table">
                  <thead>
                    <tr>
                      <th>Technológia</th>
                      <th>Verzia/Framework</th>
                      <th>Úroveň</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>C# / .NET</td>
                      <td>.NET Framework, .NET Core</td>
                      <td>
                        <span className="level expert">Expert</span>
                      </td>
                    </tr>
                    <tr>
                      <td>JavaScript</td>
                      <td>ES6+, Vanilla JS</td>
                      <td>
                        <span className="level expert">Expert</span>
                      </td>
                    </tr>
                    <tr>
                      <td>SQL</td>
                      <td>SQL Server, MySQL</td>
                      <td>
                        <span className="level expert">Expert</span>
                      </td>
                    </tr>
                    <tr>
                      <td>React</td>
                      <td>v18, Hooks, Context, Redux</td>
                      <td>
                        <span className="level expert">Expert</span>
                      </td>
                    </tr>
                    <tr>
                      <td>TypeScript</td>
                      <td>4.x, Strict mode</td>
                      <td>
                        <span className="level advanced">Advanced</span>
                      </td>
                    </tr>
                    <tr>
                      <td>Python</td>
                      <td>3.x, Django, Flask</td>
                      <td>
                        <span className="level advanced">Advanced</span>
                      </td>
                    </tr>
                    <tr>
                      <td>Java</td>
                      <td>17, Spring Boot</td>
                      <td>
                        <span className="level advanced">Advanced</span>
                      </td>
                    </tr>
                    <tr>
                      <td>React Native</td>
                      <td>0.72, Expo</td>
                      <td>
                        <span className="level advanced">Advanced</span>
                      </td>
                    </tr>
                    <tr>
                      <td>Kotlin</td>
                      <td>Android Development</td>
                      <td>
                        <span className="level intermediate">Intermediate</span>
                      </td>
                    </tr>
                    <tr>
                      <td>Visual FoxPro</td>
                      <td>Legacy Systems</td>
                      <td>
                        <span className="level expert">Expert</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="skills-category">
              <h3>Databázy</h3>
              <div className="skills-tags">
                <span className="skill-tag expert">SQL Server</span>
                <span className="skill-tag expert">MySQL</span>
                <span className="skill-tag advanced">PostgreSQL</span>
                <span className="skill-tag intermediate">Oracle</span>
                <span className="skill-tag advanced">SQLite</span>
              </div>
            </div>

            <div className="skills-category">
              <h3>Web technológie</h3>
              <div className="skills-tags">
                <span className="skill-tag advanced">React.js</span>
                <span className="skill-tag advanced">HTML5/CSS3</span>
                <span className="skill-tag intermediate">PHP</span>
                <span className="skill-tag intermediate">ASP.NET</span>
                <span className="skill-tag advanced">Bootstrap</span>
                <span className="skill-tag intermediate">Material UI</span>
              </div>
            </div>

            <div className="skills-category">
              <h3>Desktop aplikácie</h3>
              <div className="skills-tags">
                <span className="skill-tag expert">WPF</span>
                <span className="skill-tag expert">WinForms</span>
                <span className="skill-tag expert">C# Desktop</span>
                <span className="skill-tag intermediate">Java</span>
              </div>
            </div>

            <div className="skills-category">
              <h3>Natívne aplikácie</h3>
              <div className="skills-tags">
                <span className="skill-tag advanced">Android (Kotlin)</span>
                <span className="skill-tag intermediate">iOS (Swift)</span>
                <span className="skill-tag expert">React Native</span>
                <span className="skill-tag intermediate">Expo</span>
              </div>
            </div>

            <div className="skills-category">
              <h3>Nástroje a technológie</h3>
              <div className="skills-tags">
                <span className="skill-tag advanced">Git/SVN</span>
                <span className="skill-tag intermediate">Docker</span>
                <span className="skill-tag advanced">Windows Server</span>
                <span className="skill-tag intermediate">Linux</span>
                <span className="skill-tag advanced">IIS</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Services
      <section className="main-services">
        <div className="container">
          <h2>Naše hlavné služby</h2>
          <div className="services-grid">
            {mainServices.map((service) => (
              <div key={service.id} className="service-card" style={{ background: service.gradient }}>
                <div className="service-header">
                  <div className="service-icon">
                    <service.icon />
                  </div>
                  <div className="service-price">{service.price}</div>
                </div>
                
                <div className="service-info">
                  <h3>{service.title}</h3>
                  <h4>{service.subtitle}</h4>
                  <p>{service.description}</p>
                </div>

                <div className="service-features">
                  <h5>Čo zahŕňa:</h5>
                  <ul>
                    {service.features.slice(0, 4).map((feature, index) => (
                      <li key={index}>
                        <FaCheckCircle />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="service-actions">
                  <Link to={service.link} className="btn btn-primary">
                    Zistiť viac <FaArrowRight />
                  </Link>
                  <Link to="/kontakt" className="btn btn-secondary">
                    Získať ponuku
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* Support Levels
      <section className="support-section">
        <div className="container">
          <h2>Úrovne technickej podpory</h2>
          <div className="support-grid">
            {supportLevels.map((level, index) => (
              <div key={index} className={`support-card ${level.popular ? 'popular' : ''}`}>
                {level.popular && <div className="popular-badge">Najpopulárnejší</div>}
                <h3>{level.name}</h3>
                <div className="support-price">
                  <span className="price">{level.price}€</span>
                  <span className="period">{level.period}</span>
                </div>
                <p className="support-description">{level.description}</p>
                <ul className="support-features">
                  {level.features.map((feature, fIndex) => (
                    <li key={fIndex}>
                      <FaCheckCircle />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <button className="btn btn-outline" style={{ borderColor: level.color, color: level.color }}>
                  Vybrať balíček
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      Industries 
      <section className="industries-section">
        <div className="container">
          <h2>Slúžime rôznym odvetviam</h2>
          <div className="industries-grid">
            {industries.map((industry, index) => (
              <div key={index} className="industry-card">
                <div className="industry-icon">
                  <industry.icon />
                </div>
                <h4>{industry.title}</h4>
                <p>{industry.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section> */}
    </div>
  );
};

export default ServicesPage;
